import React, {
  Fragment,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from 'react'
import { Box } from '@hub/box'
import { Stack } from '@hub/stack'
import { CoreContainer } from '@hub/core-container'
import { Centre as CentreType } from '../../../../types/centre'
import {
  CentreSelector as SharedCentreSelector,
  getCentreDisplayTitle,
  Centre,
  MAX_SUGGESTIONS,
} from '../../../components/centre-selector'
import { useRecentCentres } from '../../../hooks/use-recent-centres'
import { VisitCentreTabPanel } from './visit-centre-tab-panel'
import navStyle from './navStyle.module.scss'

const centreSelectorConfig = {
  allCentresLabel: 'All centres',
  inputLabel: 'Find a centre',
  locationLabel: 'Or use your current location',
}

interface FindACentreNavPanelProps {
  centres: CentreType[]
  centre?: CentreType
}

export const FindACentreNavPanel: React.FC<FindACentreNavPanelProps> = ({
  centres,
  centre: currentCentre,
}) => {
  const [isOpenCentre, setIsOpenCentre] = useState<boolean>(false)
  const toggleOpenCentre = useCallback(() => {
    setIsOpenCentre(!isOpenCentre)
  }, [isOpenCentre])

  // Inject a `.displayTitle` field for better rendering
  const centresWithDisplayTitle: Centre[] = useMemo(
    () =>
      centres.map(centre => ({
        ...centre,
        displayTitle: getCentreDisplayTitle(centre),
      })),
    [centres]
  )

  const recentCentres: string[] = useRecentCentres()

  const [suggestedCentres, setSuggestedCentres] = useState<Centre[]>([])

  useEffect(() => {
    setSuggestedCentres(
      centresWithoutCurrent(
        currentCentre,
        recentCentres,
        centresWithDisplayTitle
      )
    )
  }, [currentCentre, recentCentres, centresWithDisplayTitle])

  return (
    <Fragment>
      <CoreContainer
        fadeOutOverflow
        as={props => (
          <Stack
            {...props}
            direction="row"
            align="center"
            shouldWrapChildren={false}
          />
        )}
        sx={{
          height: 'size-full',
          fontSize: 'font-sm',
          overflowX: 'scroll',
          position: 'relative',

          /* Hide scrollbar for Chrome, Safari and Opera */
          '&::-webkit-scrollbar': {
            display: 'none',
          },

          /* Hide scrollbar for IE, Edge and Firefox */
          msOverflowStyle: 'none' /* IE and Edge */,
          scrollbarWidth: 'none' /* Firefox */,
        }}
      >
        <VisitCentreTabPanel
          key="visit-centre"
          suggestedCentres={suggestedCentres}
          toggleOpenCentre={toggleOpenCentre}
          isOpenCentre={isOpenCentre}
        />
      </CoreContainer>
      {/* open this component for find a centre */}
      {isOpenCentre && (
        <Box sx={{ bg: 'white', position: 'relative', zIndex: 'dropdown' }}>
          <CoreContainer>
            <SharedCentreSelector
              callBackSetSuggestedCentres={setSuggestedCentres}
              centres={centres}
              navStyle={navStyle}
              config={centreSelectorConfig}
            />
          </CoreContainer>
        </Box>
      )}
    </Fragment>
  )
}

function centresWithoutCurrent(
  currentCentre: CentreType | undefined,
  recentCentres: string[],
  centresWithDisplayTitle: Centre[]
): Centre[] {
  // Filter out the current centre from suggestions
  const filteredSlugs = currentCentre
    ? recentCentres.filter(slug => slug !== currentCentre.slug)
    : recentCentres

  return (
    filteredSlugs
      // Convert it into a complete object
      .map(slug => centresWithDisplayTitle.find(centre => centre.slug === slug))
      // Clear out any bad data
      .filter(centre => Boolean(centre))
      // Limit the number of suggestions we return
      .slice(0, MAX_SUGGESTIONS) as Centre[]
  )
}
